import { useEffect, useMemo } from 'react'
import { useCampusSessionDataFetch } from './index'
import { Form, schema } from '@ggs/forms'
import { Button } from '../../components'
import { useValidators } from '@ggs/hooks'
import { useI18n } from '@ggs/i18n'
import { setAcademicPeriod, setCampus, useDispatch, useSelector } from '@ggs/store'
import { find, first } from 'lodash'

/**
 * @typedef {(values: Object<string, any>, formik: any) => Promise<void>} SubmitHandler
 */

/**
 * @typedef {Object} CampusSessionDefaults
 * @property {string} defaultCampus
 * @property {string} defaultSession
 */

/**
 * @typedef {Object} CampusSessionFormProps
 * @property {SubmitHandler} onSubmitHandler
 * @property {string=} actionLabel
 * @property {boolean=} showFieldLabels
 */

/**
 * @typedef {Object} CampusSessionFormHook
 * @property {any} FormInstance
 * @property {boolean} loading
 */

/**
 * Hook that process academic data digestion.
 * @param {CampusSessionFormProps} props
 * @return {CampusSessionFormHook}
 */
const useCampusSessionForm = ({ onSubmitHandler, actionLabel, showFieldLabels = false }) => {
  const { t } = useI18n()
  const validators = useValidators()
  const campus = useSelector((state) => state.settings.campus)
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)
  const defaultCampusCode = 'UBCV'
  const dispatch = useDispatch()

  const defaultOptions = {
    campus: campus || defaultCampusCode,
    session: academicPeriod || null,
  }

  const actions = !actionLabel
    ? []
    : [
      {
        type: 'submit',
        label: actionLabel,
        variant: Button.variant.contained,
      },
    ]

  const fieldsDefinition = {
    campus: {
      label: showFieldLabels ? t('Select Campus') : null,
      placeholder: t('Select'),
      type: schema.Forms.FieldTypes.select,
      validator: validators.nonBlank,
      showLabelOnTop: true,
      disabled: true,
    },
    session: {
      label: showFieldLabels ? t('Select Session') : null,
      placeholder: t('Select'),
      type: schema.Forms.FieldTypes.select,
      validator: validators.nonBlank,
      showLabelOnTop: true,
      disabled: true,
    },
  }

  const FormInstance = Form.useForm({
    withDraftState: false,
    formId: 'select-campus-session-form',
    className: 'select-campus-session__form',
    // @ts-ignore
    fields: fieldsDefinition,
    actions: actions,
    sx: {},
    onSubmit: onSubmitHandler,
  })

  const { loading, campusOptions, sessionOptions } = useCampusSessionDataFetch()
  const sessionValue = useMemo(() => {
    const defaultSessionExists = find(sessionOptions, { value: defaultOptions.session })
    const firstSession = first(sessionOptions)
    return defaultSessionExists ? defaultOptions.session : firstSession?.value
  }, [sessionOptions, defaultOptions])

  /**
   * Handle update form select field options.
   */
  const updateSelectFieldOptions = () => {
    const defaultOptionExists = find(campusOptions, { value: defaultOptions.campus })
    const campusValue = defaultOptionExists ? defaultOptions.campus : defaultCampusCode
    const newFields = {
      ...FormInstance.fields,
      campus: {
        ...FormInstance.fields['campus'],
        options: campusOptions,
        autoSelect: true,
        autoHighlight: true,
        disabled: false,
        value: campusValue,
      },
      session: {
        ...FormInstance.fields['session'],
        options: sessionOptions,
        autoSelect: true,
        autoHighlight: true,
        disabled: false,
        value: sessionValue,
      },
    }

    FormInstance.setFields(newFields)
  }

  useEffect(() => {
    // When campus changes, the default session is set to first on the list automatically.
    if (sessionValue && sessionValue !== academicPeriod) {
      dispatch(setAcademicPeriod(sessionValue))
    }
  }, [campus])

  useEffect(() => {
    if (campusOptions && sessionOptions) {
      updateSelectFieldOptions()
    }
  }, [campusOptions, sessionOptions, campus, academicPeriod])

  useEffect(() => {
    // console.log("CampusValuesChange:", FormInstance.context.values)
    if (FormInstance.context?.values) {
      const newCampus = FormInstance.context?.values?.campus || ''
      const newAcademicPeriod = FormInstance.context?.values?.session || ''

      if (newCampus && campus !== newCampus) {
        dispatch(setCampus(newCampus))
      }

      if (newAcademicPeriod && academicPeriod !== newAcademicPeriod) {
        dispatch(setAcademicPeriod(newAcademicPeriod))
        updateSelectFieldOptions()
      }
    }
  }, [FormInstance.context?.values])

  return {
    FormInstance,
    loading,
  }
}

export default useCampusSessionForm